/**
 * Actor orientation labels — safe, non-invented display name and role labels
 * for the AppTopBar orientation strip (W3 actor orientation).
 *
 * `JWTTokenPayload` carries no `displayName`, so `resolveActorDisplayName`
 * never fabricates a human name — it falls back to a short, non-sensitive
 * slice of `userId` or a generic account label.
 *
 * `resolveUserFacingRoleLabel` is presentation-only: it reuses existing SSOT
 * role resolvers (`resolveExperienceRoleId`, `external-entity-ontology`) and
 * introduces no new identity, permission, or role authority. Unresolvable
 * external actors fail closed to "외부 참여자" — they are never guessed as
 * Supplier or any other specific role.
 */

import type { JWTTokenPayload } from './jwtUtils';
import { resolveExperienceRoleId } from './experienceRoleResolver';
import { coerceExternalBusinessRole } from '@shared/types/external-entity-ontology';

export type ActorOrientationPayload = Pick<
  JWTTokenPayload,
  'userId' | 'entityType' | 'role' | 'permissions' | 'partnerTierId'
>;

const GENERIC_ACCOUNT_LABEL = '계정';
const SHORT_ID_LENGTH = 8;
const OWNER_ROLE = 'owner';

/**
 * Display name fallback — never invents identity. Shows a short, truncated
 * slice of `userId` when present, otherwise the generic account label.
 */
export function resolveActorDisplayName(
  payload: Pick<ActorOrientationPayload, 'userId'> | null | undefined
): string {
  const userId = payload?.userId?.trim();
  if (!userId) return GENERIC_ACCOUNT_LABEL;
  return userId.length > SHORT_ID_LENGTH ? `${userId.slice(0, SHORT_ID_LENGTH)}…` : userId;
}

/**
 * External actor orientation label — fail-safe, NEVER defaults to Supplier.
 * Detects customer/supplier/partner only from fields already present on the
 * JWT payload (role string, partnerTierId); anything undetectable falls back
 * to "외부 참여자".
 */
function resolveExternalOrientationLabel(
  role: string | null | undefined,
  partnerTierId: string | null | undefined
): string {
  if (role === 'external_customer') return '고객';
  if (role === 'external_supplier') return '공급자';

  const coerced = role ? coerceExternalBusinessRole(role) : null;
  if (coerced === 'customer') return '고객';
  if (coerced === 'supplier') return '공급자';
  if (coerced === 'buyer') return '파트너';

  if (partnerTierId) return '파트너';

  return '외부 참여자';
}

/**
 * User-facing Korean role label for orientation display only — this is not
 * an authority or permission decision surface. Internal admin detection
 * reuses the frozen `resolveExperienceRoleId` bridge (`role === 'owner'` or
 * `permissions` containing `system:admin` → `tenant_admin`); it does not
 * create a new authority path.
 */
export function resolveUserFacingRoleLabel(
  payload: ActorOrientationPayload | null | undefined
): string {
  if (!payload) return GENERIC_ACCOUNT_LABEL;

  const entityType = payload.entityType ?? null;

  if (entityType === 'internal') {
    if (payload.role === OWNER_ROLE) return '소유자';
    if (resolveExperienceRoleId(payload) === 'tenant_admin') return '관리자';
    return '직원';
  }

  if (entityType === 'user') return '회원';

  if (entityType === 'external') {
    return resolveExternalOrientationLabel(payload.role ?? null, payload.partnerTierId ?? null);
  }

  return GENERIC_ACCOUNT_LABEL;
}
